import React, { useState, useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import Globe from '../components/magicui/Globe'
import InteractiveHoverButton from '../components/magicui/InteractiveHoverButton'
import { motion, useMotionValue, useSpring } from 'framer-motion'
import GlobalCursor from '../components/GlobalCursor'
import { homeVideoAPI } from '../lib/api.js'

export default function HomePage() {
  const [isSearchBarClicked, setIsSearchBarClicked] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [videoData, setVideoData] = useState({
    videoUrl: '',
    posterUrl: '',
    autoplay: true,
    loop: true,
    muted: true
  })
  const videoRef = useRef(null)

  // Load video data from API
  useEffect(() => {
    const loadVideoData = async () => {
      try {
        const response = await homeVideoAPI.get()
        if (response.data && response.data.length > 0) {
          setVideoData(response.data[0])
          setYoutubeError(false) // Reset error state when video changes
          
        }
      } catch (error) {
        console.error('Error loading video data:', error)
        // Keep default data on error
      }
    }
    
    loadVideoData()
  }, [])

  const [youtubeError, setYoutubeError] = useState(false)

  // Ensure video plays when URL is loaded
  useEffect(() => {
    const video = videoRef.current
    if (!video || !videoData.videoUrl || isYouTubeUrl(videoData.videoUrl)) return

    console.log('🎥 Loading video:', videoData.videoUrl)
    
    // Force video to reload with new source
    video.load()
    
    const attemptPlay = () => {
      video.play()
        .then(() => console.log('✅ Video playing'))
        .catch(error => {
          console.warn('⚠️ Autoplay prevented:', error.message)
        })
    }

    // Wait for video to be ready, then play
    video.addEventListener('loadeddata', attemptPlay, { once: true })

    return () => {
      video.removeEventListener('loadeddata', attemptPlay)
    }
  }, [videoData.videoUrl])

  // Function to check if URL is a YouTube video
  const isYouTubeUrl = (url) => {
    return url && (url.includes('youtube.com') || url.includes('youtu.be'))
  }

  // Function to check if URL is a Cloudinary video
  const isCloudinaryUrl = (url) => {
    return url && url.includes('cloudinary.com')
  }

  // Function to check if URL is a video file
  const isVideoFile = (url) => {
    if (!url) return false
    const videoExtensions = ['.mp4', '.webm', '.ogg', '.mov', '.avi', '.mkv']
    return videoExtensions.some(ext => url.toLowerCase().includes(ext))
  }

  // Function to convert YouTube URL to embed URL
  const getYouTubeEmbedUrl = (url) => {
    if (!url) return ''
    
    let videoId = ''
    
    if (url.includes('youtube.com/watch?v=')) {
      videoId = url.split('v=')[1]?.split('&')[0]
    } else if (url.includes('youtu.be/')) {
      videoId = url.split('youtu.be/')[1]?.split('?')[0]
    } else if (url.includes('youtube.com/embed/')) {
      videoId = url.split('embed/')[1]?.split('?')[0]
    }
    
    if (videoId) {
      // Use more permissive parameters for better compatibility
      return `https://www.youtube.com/embed/${videoId}?autoplay=${videoData.autoplay ? 1 : 0}&mute=${videoData.muted ? 1 : 0}&controls=1&showinfo=0&rel=0&modestbranding=1&playsinline=1&enablejsapi=1&origin=${window.location.origin}`
    }
    
    return url
  }

  // Manual refresh function
  const refreshVideoData = async () => {
    try {
      const response = await homeVideoAPI.get()
      if (response.data && response.data.length > 0) {
        setVideoData(response.data[0])
        setYoutubeError(false) // Reset error state when video changes
        
      }
    } catch (error) {
      console.error('Error refreshing video data:', error)
    }
  }

  // Listen for localStorage changes to refresh video data
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'helloheman_home_video' || e.type === 'videoDataUpdated') {
        refreshVideoData()
      }
    }

    // Listen for storage events (changes from other tabs)
    window.addEventListener('storage', handleStorageChange)
    
    // Also listen for custom events (changes from same tab)
    window.addEventListener('videoDataUpdated', handleStorageChange)

    return () => {
      window.removeEventListener('storage', handleStorageChange)
      window.removeEventListener('videoDataUpdated', handleStorageChange)
    }
  }, [])

  // Add keyboard shortcut for refresh (Ctrl+R)
  useEffect(() => {
    const handleKeyDown = (event) => {
      if ((event.ctrlKey || event.metaKey) && event.key === 'r') {
        event.preventDefault()
        refreshVideoData()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [])

  // 3D Mouse tracking for video card
  const mouseX = useMotionValue(0)
  const mouseY = useMotionValue(0)
  const rotateX = useSpring(0, { stiffness: 100, damping: 30 })
  const rotateY = useSpring(0, { stiffness: 100, damping: 30 })

  // Navigation items data
  const navigationItems = [
    { href: '/', label: 'home' },
    { href: '/about', label: 'about' },
    { href: '/now', label: 'now' },
    { href: '/projects', label: 'projects' },
    { href: '/connect', label: 'connect' },
  ]

  // Filter navigation items based on search query
  const filteredItems = navigationItems.filter((item) =>
    item.label.toLowerCase().includes(searchQuery.toLowerCase()),
  )

  // Reset search when closing modal
  const handleCloseModal = () => {
    setIsSearchBarClicked(false)
    setSearchQuery('')
  }

  // Mouse tracking for 3D effect
  useEffect(() => {
    const handleMouseMove = (e) => {
      const { clientX, clientY } = e
      const { innerWidth, innerHeight } = window

      // Calculate mouse position relative to center of screen
      const centerX = innerWidth / 2
      const centerY = innerHeight / 2
      const deltaX = clientX - centerX
      const deltaY = clientY - centerY

      // Update motion values
      mouseX.set(deltaX)
      mouseY.set(deltaY)

      // Calculate rotation based on mouse position
      const maxRotation = 30 // Maximum rotation in degrees
      rotateY.set((deltaX / centerX) * maxRotation)
      rotateX.set(-(deltaY / centerY) * maxRotation)
    }

    document.addEventListener('mousemove', handleMouseMove)
    return () => document.removeEventListener('mousemove', handleMouseMove)
  }, [mouseX, mouseY, rotateX, rotateY])

  // Keyboard shortcut handler
  useEffect(() => {
    const handleKeyDown = (event) => {
      // Check for Ctrl+K (Windows/Linux) or Cmd+K (Mac)
      if ((event.ctrlKey || event.metaKey) && event.key === 'k') {
        event.preventDefault() // Prevent default browser behavior
        setIsSearchBarClicked(true)
      }

      // Close modal with Escape key
      if (event.key === 'Escape' && isSearchBarClicked) {
        handleCloseModal()
      }
    }

    // Add event listener
    document.addEventListener('keydown', handleKeyDown)

    // Cleanup event listener
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [isSearchBarClicked]) // Dependency array includes isSearchBarClicked

  return (
    <div className="h-screen w-screen fixed inset-0 overflow-hidden bg-black">
      <GlobalCursor />
      {/* Animated Stars Background - Contained within viewport */}
      <div className="absolute inset-0 stars"></div>
      <div className="absolute inset-0 stars2"></div>

      {/* Video Card - Behind Text */}
      <div
        className="absolute inset-0 z-10 flex items-start justify-center px-4 sm:px-6"
        style={{ paddingTop: 'clamp(120px, 20vh, 160px)', perspective: '1000px' }}
      >
        <motion.div
          className="bg-black/50 backdrop-blur-sm rounded-3xl border border-white/20 background-video w-full mx-auto
                     max-w-[280px] sm:max-w-md md:max-w-2xl lg:max-w-4xl
                     h-[480px] sm:h-[400px] md:h-[450px] lg:h-[500px]"
          style={{
            willChange: 'transform',
            transformStyle: 'preserve-3d',
            rotateX: rotateX,
            rotateY: rotateY,
            scale: 1.1,
          }}
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{
            duration: 0.8,
            delay: 0.1,
            ease: 'easeOut',
          }}
          data-poster-url={videoData.posterUrl || ''}
          data-video-urls={videoData.videoUrl || ''}
          data-autoplay={videoData.autoplay?.toString() || 'true'}
          data-loop={videoData.loop?.toString() || 'true'}
        >
          {isYouTubeUrl(videoData.videoUrl) && !youtubeError ? (
            <iframe
              src={getYouTubeEmbedUrl(videoData.videoUrl)}
              className="w-full h-full rounded-3xl"
              style={{
                objectFit: 'cover',
                transform: 'scale(1.1)',
              }}
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
              title="YouTube Video"
              loading="lazy"
              referrerPolicy="strict-origin-when-cross-origin"
              onError={() => setYoutubeError(true)}
              onLoad={() => setYoutubeError(false)}
            />
          ) : isYouTubeUrl(videoData.videoUrl) && youtubeError ? (
            <div className="w-full h-full rounded-3xl bg-black/50 flex items-center justify-center">
              <div className="text-center text-white">
                <p className="text-lg mb-4">YouTube video cannot be embedded</p>
                <a 
                  href={videoData.videoUrl} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="bg-[#E06100] hover:bg-[#C55000] text-white px-6 py-3 rounded-lg transition-colors"
                >
                  Watch on YouTube
                </a>
              </div>
            </div>
          ) : (
            <video
              ref={videoRef}
              autoPlay
              muted
              loop={!!videoData.loop}
              playsInline
              preload="auto"
              className="w-full h-full object-cover rounded-3xl"
              poster={videoData.posterUrl || ''}
              style={{
                objectFit: 'cover',
                transform: 'scale(1.1)',
              }}
              key={videoData.videoUrl}
            >
              <source src={videoData.videoUrl || ''} type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          )}
        </motion.div>
      </div>

      {/* Content */}
      <div className="relative z-20 flex flex-col items-center px-4 sm:px-6 pt-48 sm:pt-56 md:pt-64 pointer-events-none">
        <div className="text-center text-white max-w-4xl w-full pointer-events-auto">
              <motion.h1
                className="font-bold mb-4 sm:mb-6 font-monument-extended"
                style={{
                  lineHeight: '0.8',
                  fontSize: 'clamp(60px, 15vw, 180px)',
                }}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{
                  duration: 0.8,
                  delay: 0.2,
                  ease: 'easeOut',
                }}
              >
                artist
                <br />
                on duty
              </motion.h1>
        </div>
      </div>

      {/* Rotating Globe - After All Content */}
      <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 z-20 px-4 sm:px-6 globe-container pointer-events-auto">
        <div
          className="relative w-full max-w-4xl mx-auto"
          style={{
            height: 'clamp(400px, 60vw, 800px)',
            width: 'clamp(400px, 60vw, 800px)',
            overflow: 'hidden',
          }}
        >
          <div style={{ transform: 'translateY(540px)' }}>
            <Globe />
          </div>
        </div>
      </div>

      {/* Explore Creations Button - Separate Element on Globe */}
      <div
        className="absolute bottom-0 left-1/2 transform -translate-x-1/2 z-30 pointer-events-auto px-4 sm:px-6"
        style={{ marginBottom: 'clamp(60px, 10vw, 100px)' }}
      >
        <Link to="/projects">
          <InteractiveHoverButton>explore creations</InteractiveHoverButton>
        </Link>
      </div>

      {/* Search Modal Popup */}
      {isSearchBarClicked && (
        <div
          className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50 p-3 sm:p-4"
          onClick={handleCloseModal}
        >
          <div
            className="bg-black rounded-2xl sm:rounded-3xl shadow-2xl w-full max-w-sm sm:max-w-2xl max-h-[80vh] sm:max-h-96"
            style={{ border: '0.3px solid rgba(224, 97, 0, 1)' }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Search Input */}
            <div className="p-3 sm:p-4 border-b border-gray-800">
              <div className="relative">
                <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
                  <svg
                    className="w-4 h-4 sm:w-5 sm:h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                </div>
                <input
                  type="text"
                  placeholder="Type a command or search..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-8 sm:pl-10 pr-10 sm:pr-12 py-2.5 sm:py-3 bg-transparent text-white placeholder-gray-400 border-none outline-none text-sm sm:text-base"
                  autoFocus
                />
                <button
                  onClick={handleCloseModal}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
                >
                  <svg
                    className="w-4 h-4 sm:w-5 sm:h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
            </div>

            {/* Navigation Dropdown */}
            <div className="p-3 sm:p-4">
              <div className="space-y-2">
                {filteredItems.length > 0 ? (
                  filteredItems.map((item) => (
                    <Link
                      key={item.href}
                      to={item.href}
                      className="flex items-center px-3 py-2 text-white hover:bg-gray-900 rounded-lg transition-colors duration-200"
                      onClick={handleCloseModal}
                    >
                      <span className="text-sm sm:text-base">{item.label}</span>
                    </Link>
                  ))
                ) : (
                  <div className="text-gray-400 text-sm sm:text-base px-3 py-2">
                    No results found
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

